import moment from "moment";
import { BaseItem } from "../../axios";
import { RewardTiers } from "../../../../components/editor/inputs/programs/ProgramRewardsInput";
import { DATE_FORMAT } from "../../../..";
import { ProgramUpdateValues } from "../../../hooks/programs/useProgram";
import { sanitizeHtml } from "../../../sanitizers";
import { PhaseStatus } from "../../../../components/programs/PhaseStatusPill";

export type ProgramCreator = {
  displayName: string;
  username?: string;
  pfp?: string;
};

export type ProgramAPIResponse = {
  program_id: number;
  is_activated: boolean;
  user_id: number;
  is_delete: boolean;
  private: boolean;
  private_access_users: number[];
  type: ProgramType;
  triage_service_opted: string;
  payout_range: string;
  program_title: string;
  description: string;
  scope: string;
  out_of_scope: string;
  known_vuln: string;
  other: string;
  targets: string;
  vpn: string;
  credentials: string;
  reward_policy: string;
  tos: string;
  pfp: string;
  createdAt: string;
  updatedAt: string;
  creator?: ProgramCreator;
  testing_type?: string;
  environment_type?: string;
  compliance_type?: string;
  other_compliance_type?: string;
  expected_start_date?: string;
  expected_end_date?: string;
  slack_channel_link: string;
  notification_methods?: string[];
  attachments?: string[];
  test_lead?: string;
  prepared_by?: string;
  reviewed_by?: string;
  approved_by?: string;
  jira_url?: string;
  jira_email?: string;
  jira_api_token?: string;
  jira_project_key?: string;
  phase_status?: 'pretesting' | 'active_reporting' | PhaseStatus;
  architecture_version?: number;
  scheduling_type?: 'one_off' | 'continuous';
  recurrence?: 'monthly' | 'quarterly' | 'half_yearly' | 'yearly';
  asset_name?: string;
  company_id?: number | string;
  asset?: {
    id: string;
    company_id: number;
    name: string;
    [key: string]: any;
  };
};

export type CTBProgram = {
  title: string;
  isActivated: boolean;
  isDelete: boolean;
  private: boolean;
  triaged: boolean;
  targets: ProgramTarget[];
  updatedAt: string;
  createdAt: string;
  userId?: number;
  privateAccessUsers?: number[];
  type?: ProgramType;
  payoutRange?: RewardTiers;
  description?: string;
  scope?: string;
  knownVulnerabilities?: string;
  other?: string;
  numTargets?: string;
  vpn?: string;
  credentials?: string;
  rewardPolicy?: string;
  termsOfService?: string;
  profilePicture?: string;
  outOfScope?: string;
  creator?: ProgramCreator;
  testing_type?: string;
  environment_type?: string;
  compliance_type?: string;
  other_compliance_type?: string;
  expected_start_date?: string;
  expected_end_date?: string;
  slack_channel_link: string;
  notification_methods?: string[];
  attachments?: string[];
  test_lead?: string;
  prepared_by?: string;
  reviewed_by?: string;
  approved_by?: string;
  // Jira integration fields
  jira_url?: string;
  jira_email?: string;
  jira_api_token?: string;
  jira_project_key?: string;
  phase_status?: 'pretesting' | 'active_reporting' | PhaseStatus;
  asset_id?: string;
  asset_name?: string;
  target_ids?: any;
  architecture_version?: number;
  scheduling_type?: 'one_off' | 'continuous';
  recurrence?: 'monthly' | 'quarterly' | 'half_yearly' | 'yearly';
  one_off_start_date?: string;
  one_off_end_date?: string;
  continuous_start_date?: string;
  continuous_end_date?: string;
  release_notes?: string;
  custom_roe?: string;
  linked_vault_ids?: number[];
  companyId?: number;
  company_name?: string;
  asset_documentation?: {
    architecture_diagram_url?: string;
    product_documentation_url?: string;
    runbook_url?: string;
    threat_model_url?: string;
    additional_notes?: string;
    documentation_files?: any;
  };
  asset?: any;
  vault_configs?: any[];
  unsubmittedScopeCount?: number;
  submittedButUnverifiedCount?: number;
  runCount?: number;
} & BaseItem;

export enum TargetType {
  WEB = "Web",
  IOS = "iOS",
  ANDROID = "Android",
  API = "API",
  WEB3 = "Web3",
  CODE = "Source Code",
  PROTOCOL = "Protocol",
  DATABASE = "Database",
  NETWORK = "Network/Infrastructure",
  CONTRACT = "Smart Contract",
  OTHER = "Other"
}

export type ProgramTarget = { targetName: string; targetType: TargetType };

export enum ProgramType {
  BugBounty = "Bug Bounty",
  VDP = "VDP",
  PTAAS = "PTAAS"
}

const parsePayoutRange = (range?: string): RewardTiers | undefined => {
  const values = range?.split(",").map(v => parseInt(v));

  return values && values.length === 4
    ? {
      low: values[0],
      medium: values[1],
      high: values[2],
      critical: values[3]
    }
    : undefined;
};

export const parseProgramDetails = (
  program: ProgramAPIResponse
): CTBProgram => {
  if (!program) {
    return null as any;
  }

  // Parse attachments properly
  let attachments: string[] = [];
  if (program.attachments) {
    if (typeof program.attachments === "string") {
      try {
        attachments = JSON.parse(program.attachments);
      } catch (e) {
        console.error("Failed to parse attachments:", e);
        attachments = [];
      }
    } else if (Array.isArray(program.attachments)) {
      attachments = program.attachments;
    }
  }

  // Parse targets properly
  let targets: any[] = [];
  if (program.targets) {
    if (typeof program.targets === "string") {
      try {
        targets = JSON.parse(program.targets) || [];
      } catch (e) {
        console.error("Failed to parse targets:", e);
        targets = [];
      }
    } else if (Array.isArray(program.targets)) {
      targets = program.targets;
    }
  }

  // Parse target_ids properly
  let target_ids: any = [];
  const rawTargetIds = (program as any).target_ids;
  if (rawTargetIds) {
    if (typeof rawTargetIds === "string") {
      try {
        target_ids = JSON.parse(rawTargetIds);
      } catch (e) {
        console.error("Failed to parse target_ids:", e);
        target_ids = [];
      }
    } else if (Array.isArray(rawTargetIds)) {
      target_ids = rawTargetIds;
    }
  }

  return {
    id: program.program_id,
    isActivated: program.is_activated,
    userId: program.user_id,
    isDelete: program.is_delete,
    private: program.private,
    privateAccessUsers: program.private_access_users,
    type: program.type,
    triaged: program.triage_service_opted === "1",
    payoutRange: parsePayoutRange(program.payout_range),
    title: program.program_title,
    description: program.description,
    scope: program.scope,
    knownVulnerabilities: program.known_vuln,
    other: program.other,
    numTargets: targets as any,
    vpn: program.vpn,
    credentials: program.credentials,
    rewardPolicy: program.reward_policy,
    termsOfService: program.tos,
    profilePicture: program.pfp,
    outOfScope: program.out_of_scope,
    targets: targets,
    creator: program.creator,
    updatedAt: program.updatedAt ? moment(program.updatedAt).format(DATE_FORMAT) : "",
    createdAt: program.createdAt ? moment(program.createdAt).format(DATE_FORMAT) : "",
    testing_type: program.testing_type,
    compliance_type: program.compliance_type,
    other_compliance_type: program.other_compliance_type,
    environment_type: program.environment_type,
    expected_start_date: (program as any).effective_start_date || program.expected_start_date,
    expected_end_date: (program as any).effective_end_date || program.expected_end_date,
    slack_channel_link: program.slack_channel_link,
    notification_methods: program.notification_methods,
    attachments: attachments, // Use the properly parsed attachments
    test_lead: program.test_lead,
    prepared_by: program.prepared_by,
    reviewed_by: program.reviewed_by,
    approved_by: program.approved_by,
    // Add Jira integration fields
    jira_url: program.jira_url,
    jira_email: program.jira_email,
    jira_api_token: program.jira_api_token,
    jira_project_key: program.jira_project_key,
    phase_status: program.phase_status || 'pretesting', // Default to pretesting for new programs
    asset_id: (program as any).asset_id,
    asset_name: (program as any).asset_name,
    target_ids: target_ids,
    architecture_version: (program as any).architecture_version,
    scheduling_type: (program as any).scheduling_type || 'one_off',
    recurrence: (program as any).recurrence,
    companyId: (program as any).company_id,
    company_name: (program as any).company_name,
    asset_documentation: (program as any).asset_documentation,
    release_notes: (program as any).release_notes || '',
    custom_roe: (program as any).roe?.custom_roe || '',
    one_off_start_date: (program as any).scheduling_type === 'one_off' ? program.expected_start_date : '',
    one_off_end_date: (program as any).scheduling_type === 'one_off' ? program.expected_end_date : '',
    continuous_start_date: (program as any).scheduling_type === 'continuous' ? program.expected_start_date : '',
    continuous_end_date: (program as any).scheduling_type === 'continuous' ? program.expected_end_date : '',
    // Derive linked_vault_ids from vault_configs if not directly present
    linked_vault_ids: (program as any).linked_vault_ids || ((program as any).vault_configs || []).map((vc: any) => vc.vault_id) || [],
    asset: (program as any).asset,
    unsubmittedScopeCount: (program as any).unsubmitted_scope_count || 0,
    submittedButUnverifiedCount: (program as any).submitted_but_unverified_count || 0,
    runCount: (program as any).run_count || 0,
  };
};

/**
 * Convert program update fields into form data
 * to be used in an API request
 */
export const prepareProgramFormData = ({
  details,
  icon,
  attachments
}: {
  details: ProgramUpdateValues & {
    payoutRange?: number[];
    triaged?: boolean;
    existingAttachments?: string[];
    vaultConfigs?: any[];
  };
  icon?: Blob;
  attachments?: Blob[];
}) => {
  // Format payload for backend API
  const body = {
    program_title: details.title,
    description: details.description,
    scope: details.scope,
    out_of_scope: details.outOfScope,
    known_vuln: details.knownVulnerabilities,
    other: details.other,
    payout_range: details.payoutRange?.join(","),
    targets: details.targets,
    // Optional list of Asset IDs from inventory; backend will derive targets (legacy)
    asset_ids: details.assetIds,
    // Enhanced: linked assets with target/resource selection
    linked_assets: details.linkedAssets,
    vpn: details.vpn,
    tos: details.termsOfService,
    credentials: details.credentials,
    reward_policy: details.rewardPolicy,
    type: details.type,
    private: details.private,
    triage_service_opted: details.triaged,
    compliance_type: details.complianceType,
    other_compliance_type: details.otherComplianceType,
    testing_type: details.testingType,
    environment_type: details.environmentType,
    expected_start_date: details.expectedStartDate,
    expected_end_date: details.expectedEndDate,
    notification_methods: details.notification_methods,
    // Individual Jira fields
    jira_url: details.jira_url,
    jira_email: details.jira_email,
    jira_api_token: details.jira_api_token,
    jira_project_key: details.jira_project_key,
    // Slack channel link
    slack_channel_link: details.slack_channel_link,
    // Include the existing attachments that weren't deleted
    existing_attachments: details.existingAttachments || [],
    // Optional owner override for admins
    owner_id: details.owner_id,
    // Ensure company linkage when created by admin
    company_id: details.company_id,
    // V2 Architecture fields
    architecture_version: (details as any).architecture_version || 2,
    scheduling_type: (details as any).scheduling_type,
    recurrence: (details as any).recurrence,
    one_off_start_date: (details as any).one_off_start_date,
    one_off_end_date: (details as any).one_off_end_date,
    continuous_start_date: (details as any).continuous_start_date,
    continuous_end_date: (details as any).continuous_end_date,
    release_notes: (details as any).release_notes,
    custom_roe: (details as any).custom_roe,
    asset_id: (details as any).asset_id,
    target_ids: (details as any).target_ids,
    vault_configs: details.vaultConfigs || [],
    status: (details as any).status,
  };

  const formData = new FormData();
  formData.append("data", JSON.stringify(body));

  // Only append profile picture if provided
  if (icon) {
    formData.append("program_pfp", icon);
  }

  // Append all attachments if provided
  if (attachments && attachments.length > 0) {
    attachments.forEach(file => {
      formData.append("attachments", file);
    });
  }

  return formData;
};

export enum NotificationMethods {
  CTB = "notify-ctb",
  SLACK = "notify-slack",
  JIRA = "notify-jira"
}

export type ProgramOverview = {
  id: number;
  status: string;
  totalReports: number;
  testingPeriod: string | null;
  testingStatus: string;
  createdAt: string;
  description: string;
  expected_start_date?: string;
  type?: string;
  companyId?: number;
  vaultConfigs?: any[]; // To handle credential linking
  asset_documentation?: {
    architecture_diagram_url?: string;
    product_documentation_url?: string;
    runbook_url?: string;
    threat_model_url?: string;
    additional_notes?: string;
    documentation_files?: any;
  };
};

export type UserDetails = {
  username: string;
  profilePicture: string;
  about: string;
  displayName: string;
};

export type ProgramOverviewResponse = {
  program: ProgramOverview;
  user: UserDetails;
};

export const parseProgramOverview = (data: any): ProgramOverviewResponse => {
  const about = data.user?.about?.trim();
  const rawDescription =
    data.program?.description?.trim() || "No description available";

  return {
    program: {
      id: data.program?.id ?? 0,
      status: data.program?.status ?? "Inactive",
      totalReports: data.program?.total_reports ?? 0,
      testingPeriod: data.program?.testing_period ?? "N/A",
      testingStatus: data.program?.testing_status ?? "N/A",
      type: data.program?.type,
      expected_start_date: data.program?.expected_start_date
        ? moment(data.program.expected_start_date).format("YYYY-MM-DD")
        : "Invalid date",
      createdAt: data.program?.created_at
        ? moment(data.program.created_at).format("YYYY-MM-DD")
        : "Invalid date",
      description: sanitizeHtml(rawDescription).__html, // Sanitize HTML before storing
      companyId: data.user?.company_id,
      asset_documentation: data.program?.asset_documentation
    },
    user: {
      username: data.user?.username ?? "Unknown",
      profilePicture: data.user?.profile_picture ?? "",
      about: about || rawDescription, // Fallback to description if about is empty
      displayName: data.user?.display_name ?? data.user?.username ?? "Unknown"
    }
  };
};
